import { Control, Controller, useFormContext } from 'react-hook-form';
import { FC } from 'react';
import i18next from 'i18next';

import { AboutFormData } from '../../types';
import { UserRole } from '../../../../../../../types/user';
import { MAX_NUMBER_OF_YEARS_IN_THE_FUTURE } from '../../../../../utils/validation';
import { SoknadSteg } from '../../../../../utils/registerEnums';
import { useUser } from '../../../../../../../hooks/useUser';

import { YearNumberField } from './yearNumberField';

interface Props {
  control: Control<AboutFormData>;
}

// Field validation
export const validateYear = (year: number | undefined, formValues: AboutFormData, userRole?: UserRole) => {
  // Require a 4 digit value
  if (!year || 1000 > year) {
    return i18next.t('register.bygningsdetaljer.byggear.error');
  }

  const currentYear = new Date().getFullYear();
  const isProfessional = userRole === UserRole.PROFESJONELL;

  const constructionYear = formValues.bygginfo?.year || 0;
  // Check if year is before building was built
  if (year < constructionYear) {
    return i18next.t('register.parameters.error.year_min');
  }

  // Non experts cant register buildings built in the future
  if (!isProfessional && year > currentYear) {
    return i18next.t('register.errorMessage.field.byggAr_max');
  }

  // Professional users are allowed to register buildings that have not been built
  const allowedConstructionYear = currentYear + MAX_NUMBER_OF_YEARS_IN_THE_FUTURE;

  if (isProfessional && year > allowedConstructionYear) {
    return i18next.t('register.errorMessage.field.byggAr_maxProfessional');
  }
};

export const ConstructionYearField: FC<Props> = ({ control }) => {
  const { getValues } = useFormContext<AboutFormData>();

  const soknadSteg = getValues().soknadSteg;
  const yearFieldDisabled = !!soknadSteg && soknadSteg !== SoknadSteg.Steg1;

  const { user } = useUser();
  return (
    <Controller
      control={control}
      rules={{
        validate: (year, formValues) => validateYear(year, formValues, user?.rolle)
      }}
      name="bygginfo.year"
      render={({ field, fieldState, ...rest }) => (
        <YearNumberField
          disabled={yearFieldDisabled}
          labelKey="register.bygningsdetaljer.byggear.label"
          informationKey="register.bygningsdetaljer.byggear.information"
          error={fieldState.error}
          value={field.value}
          onChange={(year) => {
            field.onChange(year);
          }}
          {...rest}
        />
      )}
    />
  );
};
